import React, { useEffect, useMemo, useRef, useState } from 'react';
import i18n from '../../../../../i18n';
import { METRICS } from '../../../data/metrics';
import { abbrevLabel } from '../../shared/Trend';
import {
  OpsHoverTipPortal,
  useOpsHoverTip,
} from '../../shared/OpsHoverTip';

const PAIR_PRIMARY = 'var(--accent)';
/** Neutral second series; status colours stay on deltas. */
const PAIR_SECONDARY = 'var(--petrol-ink)';
const PAIR_COMPARE = 'var(--ink-3)';

function DrillBarSlot({
  value,
  fmtVal,
  metricLabel,
  deltaPct,
  deltaGood,
  heightPct,
  fillColor,
  valueClassName,
  fillClassName,
  showValue = true,
  showMetricLabel = true,
  showDelta = true,
  compareDeltaPct,
  showCompare = false,
}: {
  value: number;
  fmtVal: (v: number) => string;
  metricLabel?: string;
  deltaPct: number | null;
  deltaGood: boolean;
  heightPct: number;
  fillColor: string;
  valueClassName?: string;
  fillClassName?: string;
  showValue?: boolean;
  showMetricLabel?: boolean;
  showDelta?: boolean;
  /** Signed delta vs the compare period for this bucket (pct or pt). */
  compareDeltaPct?: number | null;
  showCompare?: boolean;
}) {
  return (
    <div className="drill-bar-slot">
      {showValue && (
        <div className="drill-bar-metric-head">
          <span
            className={`drill-bar-val${valueClassName ? ` ${valueClassName}` : ''}`}
          >
            {fmtVal(value)}
          </span>
          {showMetricLabel && metricLabel && (
            <span className="drill-bar-metric-lab">{metricLabel}</span>
          )}
          {showDelta && deltaPct != null && (
            <span
              className="drill-bar-delta"
              style={{
                color: deltaGood ? 'var(--st-steady)' : 'var(--st-alert)',
              }}
            >
              {deltaPct >= 0 ? '+' : ''}
              {deltaPct}%
            </span>
          )}
          {showCompare && compareDeltaPct != null && (
            <span
              className="drill-bar-delta dcrux-cmp-chip"
              title={i18n.t('performance:drillUi.vsComparePeriod')}
            >
              {compareDeltaPct >= 0 ? '+' : ''}
              {compareDeltaPct}%
            </span>
          )}
        </div>
      )}
      <span className="drill-bar-track">
        <span
          className={`drill-bar-fill${fillClassName ? ` ${fillClassName}` : ''}`}
          style={{ height: `${heightPct}%`, background: fillColor }}
        />
      </span>
    </div>
  );
}

function fmtDelta(deltaPct: number | null): string {
  if (deltaPct == null) return '';
  return `${deltaPct >= 0 ? '+' : ''}${deltaPct}%`;
}

/** Full multi-bucket bar chart used in Trend / Blocked Advanced. */
export function DrillBucketBarChart({
  series,
  secondarySeries,
  compareSeries,
  seriesLabel,
  secondarySeriesLabel,
  compareLabel,
  compareBucketLabels,
  bucketLabels,
  fmtVal,
  fmtVal2,
  metricKey,
  secondaryKey,
  bare = false,
}: {
  series: number[];
  secondarySeries?: number[];
  /** Compare-period series, aligned 1:1 with `series`. Shown as a per-bucket delta chip. */
  compareSeries?: number[];
  seriesLabel?: string;
  secondarySeriesLabel?: string;
  /** Label for the compare legend; names a bucket's compare value only as the fallback. */
  compareLabel?: string;
  /** The compare window of each bucket, aligned 1:1 with `compareSeries` ("29 Sept – 5 Oct 2025"). */
  compareBucketLabels?: string[];
  bucketLabels?: string[];
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  metricKey: string;
  secondaryKey?: string;
  /** Skip outer drill-panel chrome when nested in DrillSection. */
  bare?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const [colWidth, setColWidth] = useState(0);
  const { tip, show, move, hide } = useOpsHoverTip();
  const n = series.length;

  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const measure = () =>
      setColWidth(n ? Math.max(0, (el.clientWidth - 6 * (n - 1)) / n) : 0);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [n]);

  const valStrs = useMemo(
    () => [
      ...series.map(v => fmtVal(v)),
      ...(secondarySeries ?? []).map(v => (fmtVal2 ? fmtVal2(v) : '')),
    ],
    [series, secondarySeries, fmtVal, fmtVal2],
  );

  if (n < 2) return null;
  const paired = !!secondarySeries?.length && !!fmtVal2;
  const hasCompare =
    !!compareSeries && compareSeries.length === series.length;
  const max = Math.max(
    ...series.map(v => Math.abs(v)),
    ...(secondarySeries ?? []).map(v => Math.abs(v)),
    ...(compareSeries ?? []).map(v => Math.abs(v)),
    0.0001,
  );
  const goodHighPrimary = METRICS[metricKey]?.better === 'high';
  const goodHighSecondary = secondaryKey
    ? METRICS[secondaryKey]?.better === 'high'
    : goodHighPrimary;
  const primaryLabel = METRICS[metricKey]?.short;
  const secondaryLabel = secondaryKey ? METRICS[secondaryKey]?.short : undefined;
  const isPctMetric = METRICS[metricKey]?.unit === '%';

  const slotsPerCol = 1 + (hasCompare ? 1 : 0) + (paired ? 1 : 0);
  const slotWidth =
    slotsPerCol > 1
      ? Math.max(0, (colWidth - 4 * (slotsPerCol - 1)) / slotsPerCol)
      : colWidth;
  const maxValLen = Math.max(...valStrs.map(s => s.length), 1);
  const maxLabLen = Math.max(
    0,
    ...(bucketLabels ?? []).slice(0, n).map(s => (s ?? '').length),
  );
  const needVal = maxValLen * 5 + 3;
  const needLab = maxLabLen * 5 + 3;
  const measured = colWidth > 0;
  const showHead = measured ? slotWidth >= needVal : n <= 6;
  const showMetricLabels = showHead && slotWidth >= 42;
  const showDeltas = showHead && slotWidth >= 34;
  const showCompare = hasCompare && showHead && slotWidth >= 34;
  const showCaptions = measured ? colWidth >= needLab : n <= 6;
  const showShortCaptions = !showCaptions && measured && colWidth >= 18;

  const body = (
    <>
      {!bare && (seriesLabel || secondarySeriesLabel) && (
        <div className="dp-head">
          <h3>
            {seriesLabel}
            {paired && secondarySeriesLabel ? ` · ${secondarySeriesLabel}` : ''}
          </h3>
        </div>
      )}
      {hasCompare && (
        <div className="drill-bar-legend">
          <span className="drill-bar-legend-item">
            <span className="drill-bar-legend-swatch drill-bar-legend-cur" />
            {i18n.t('performance:drillUi.thisPeriod')}
          </span>
          <span className="drill-bar-legend-item">
            <span className="drill-bar-legend-swatch drill-bar-legend-cmp" />
            {compareLabel ?? i18n.t('performance:drillUi.compare')}
          </span>
        </div>
      )}
      <div className={`drill-bars${showHead ? '' : ' dense'}`} ref={trackRef}>
        {series.map((v, i) => {
          const v2 = secondarySeries?.[i];
          const hPct = Math.max(4, (Math.abs(v) / max) * 100);
          const hPct2 =
            v2 != null ? Math.max(4, (Math.abs(v2) / max) * 100) : null;
          const prev = i > 0 ? series[i - 1] : null;
          const prev2 = i > 0 ? secondarySeries?.[i - 1] : null;
          let deltaPct: number | null = null;
          if (prev != null && prev !== 0) {
            deltaPct = +((v / prev - 1) * 100).toFixed(1);
          }
          let deltaPct2: number | null = null;
          if (prev2 != null && prev2 !== 0 && v2 != null) {
            deltaPct2 = +((v2 / prev2 - 1) * 100).toFixed(1);
          }
          const deltaGood =
            deltaPct != null
              ? goodHighPrimary
                ? deltaPct >= 0
                : deltaPct <= 0
              : true;
          const deltaGood2 =
            deltaPct2 != null
              ? goodHighSecondary
                ? deltaPct2 >= 0
                : deltaPct2 <= 0
              : true;
          const caption = bucketLabels?.[i];
          const cmp = hasCompare ? compareSeries?.[i] : undefined;
          const cmpName =
            compareBucketLabels?.[i] || compareLabel || i18n.t('performance:drillUi.compare');
          let compareDeltaPct: number | null = null;
          if (cmp != null && cmp !== 0) {
            compareDeltaPct = isPctMetric
              ? +(v - cmp).toFixed(1)
              : +((v / cmp - 1) * 100).toFixed(1);
          }
          const tipParts: string[] = [];
          tipParts.push(
            `${hasCompare ? i18n.t('performance:drillUi.thisPeriod') : (primaryLabel ?? metricKey)}: ${fmtVal(v)}${deltaPct != null && !hasCompare ? ` (${fmtDelta(deltaPct)})` : ''}`.trim(),
          );
          if (paired && v2 != null && fmtVal2) {
            tipParts.push(
              `${fmtVal2(v2)} ${secondaryLabel ?? secondaryKey ?? ''}${deltaPct2 != null ? ` (${fmtDelta(deltaPct2)})` : ''}`.trim(),
            );
          }
          if (cmp != null) {
            tipParts.push(
              `${cmpName}: ${fmtVal(cmp)}${compareDeltaPct != null ? ` (${fmtDelta(compareDeltaPct)})` : ''}`,
            );
          }
          const tipText = tipParts.join(' · ');
          return (
            <div
              className="drill-bar-col"
              key={i}
              onMouseEnter={e => show(e, { title: caption, text: tipText })}
              onMouseMove={move}
              onMouseLeave={hide}
            >
              <div
                className={`drill-bar-pair${hasCompare && cmp != null ? ' drill-bar-pair-cmp' : ''}${paired || (hasCompare && cmp != null) ? '' : ' drill-bar-pair-single'}`}
              >
                <DrillBarSlot
                  value={v}
                  fmtVal={fmtVal}
                  metricLabel={paired ? primaryLabel : undefined}
                  deltaPct={deltaPct}
                  deltaGood={deltaGood}
                  heightPct={hPct}
                  fillColor={PAIR_PRIMARY}
                  showValue={showHead}
                  showMetricLabel={showMetricLabels}
                  showDelta={showDeltas && !hasCompare}
                  compareDeltaPct={compareDeltaPct}
                  showCompare={showCompare}
                />
                {hasCompare && cmp != null && (
                  <DrillBarSlot
                    value={cmp}
                    fmtVal={fmtVal}
                    metricLabel={
                      showMetricLabels
                        ? (compareBucketLabels?.[i] || compareLabel || i18n.t('performance:drillUi.prev'))
                        : undefined
                    }
                    deltaPct={null}
                    deltaGood
                    heightPct={Math.max(4, (Math.abs(cmp) / max) * 100)}
                    fillColor={PAIR_COMPARE}
                    valueClassName="drill-bar-val-cmp"
                    fillClassName="drill-bar-fill-cmp"
                    showValue={showHead}
                    showMetricLabel={showMetricLabels}
                    showDelta={false}
                  />
                )}
                {paired && v2 != null && fmtVal2 && hPct2 != null && (
                  <DrillBarSlot
                    value={v2}
                    fmtVal={fmtVal2}
                    metricLabel={secondaryLabel}
                    deltaPct={deltaPct2}
                    deltaGood={deltaGood2}
                    heightPct={hPct2}
                    fillColor={PAIR_SECONDARY}
                    valueClassName="drill-bar-val-2"
                    fillClassName="drill-bar-fill-2"
                    showValue={showHead}
                    showMetricLabel={showMetricLabels}
                    showDelta={showDeltas}
                  />
                )}
              </div>
              {caption && showCaptions && (
                <span className="drill-bar-caption" title={caption}>
                  {caption}
                </span>
              )}
              {caption && showShortCaptions && (
                <span className="drill-bar-caption" title={caption}>
                  {abbrevLabel(caption)}
                </span>
              )}
            </div>
          );
        })}
      </div>
      <OpsHoverTipPortal tip={tip} />
    </>
  );

  if (bare) return <div className="drill-barchart">{body}</div>;
  return <div className="drill-panel drill-barchart">{body}</div>;
}
